import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewEncapsulation, ElementRef, ViewChild, AfterViewInit, OnDestroy, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService } from '../../../../core/services/theme.service';

export interface IsometricBarGroupItem {
  key?: string;
  value: number;
  color?: string;
}

export interface IsometricBarData {
  label: string;
  value?: number; // For single bar
  color?: string; // For single bar
  success?: number; // For stacked
  failure?: number; // For stacked
  values?: IsometricBarGroupItem[]; // For grouped
}

@Component({
  selector: 'app-isometric-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './isometric-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None
})
export class IsometricBarComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() data: IsometricBarData[] = [];
  @Input() width: number | 'auto' = 300;
  @Input() height: number | 'auto' = 250;
  @Input() successColor: string = '#10b981';
  @Input() failureColor: string = '#ef4444';

  // New Inputs
  @Input() barWidth: number = 35;
  @Input() barDepth: number = 20;
  @Input() angle: number = 30;
  @Input() borderRadius: number = 0;
  @Input() showLegend: boolean = false;
  @Input() showGrid: boolean = false;

  // Opacity controls
  @Input() successOpacity: number = 0.5;
  @Input() failureOpacity: number = 0.9;
  @Input() sideFaceDarkenFactor: number = 0.25;

  @Input() showYAxis: boolean = false;
  @Input() showTooltip: boolean = false;
  @Input() showBarValues: boolean = true;
  @Input() xAxisTickFormat: any = null;
  @Input() margins: any = { top: 40, right: 30, bottom: 50, left: 60 };

  // Legend
  @Input() legendData: { label: string; color?: string }[] = [];
  @Input() legendGap: number = 100;
  @Input() legendPosition: 'left' | 'center' | 'right' = 'center';

  // New axis line and highlight controls
  @Input() showXAxisLine: boolean = false;
  @Input() showYAxisLine: boolean = false;
  @Input() topHighlightFactor: number = 0.5;
  @Input() showPercentage: boolean = true;
  @Input() xAxisShowOnlyFirstLast: boolean = false;
  @Input() hoverOpacity: number = 0.05;
  @Input() showBgRect: boolean = true;
  @Input() reducedHighlight: boolean = false;
  @Input() xAxisPadding: number = 0;
  @Input() innerPadding: number = 0.2;
  @Input() fitBarToBandwidth: boolean = false;
  @Input() maxBarWidth: number = 0;

  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  @ViewChild('tooltip', { static: false }) tooltipElement!: ElementRef;

  // Unique ID for gradients
  private chartId = Math.random().toString(36).substring(2);
  private resizeObserver: ResizeObserver | null = null;
  private renderTimeout: any;

  // Actual dimensions used for D3
  private actualWidth: number = 300;
  private actualHeight: number = 250;

  constructor(private themeService: ThemeService) { }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
    this.renderChart();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined' && this.chartContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.renderTimeout) clearTimeout(this.renderTimeout);
        this.renderTimeout = setTimeout(() => {
          this.renderChart();
        }, 100);
      });
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.renderTimeout) clearTimeout(this.renderTimeout);
    this.renderTimeout = setTimeout(() => {
      this.renderChart();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['data'] || changes['showYAxis'] || changes['showTooltip'] || changes['xAxisPadding'] || changes['margins'] || changes['showLegend'] || changes['legendData'] || changes['innerPadding'] || changes['fitBarToBandwidth'] || changes['maxBarWidth']) && !changes['data']?.firstChange) {
      this.renderChart();
    }
    if ((changes['width'] && !changes['width'].firstChange) ||
      (changes['height'] && !changes['height'].firstChange) ||
      (changes['barWidth'] && !changes['barWidth'].firstChange) ||
      (changes['angle'] && !changes['angle'].firstChange) ||
      (changes['barDepth'] && !changes['barDepth'].firstChange) ||
      (changes['borderRadius'] && !changes['borderRadius'].firstChange) ||
      (changes['sideFaceDarkenFactor'] && !changes['sideFaceDarkenFactor'].firstChange)
    ) {
      this.renderChart();
    }
  }

  private renderChart(): void {
    if (!this.chartContainer) return;

    const container = d3.select(this.chartContainer.nativeElement);
    container.selectAll('svg').remove(); // only remove svg, keep tooltip

    if (!this.data || this.data.length === 0) return;

    const isDark = this.themeService.getTheme() === 'dark';
    const textColor = isDark ? '#e4e4e7' : '#374151';
    const labelColor = isDark ? '#a1a1aa' : '#6b7280';

    // Get current container dimensions
    const rect = this.chartContainer.nativeElement.getBoundingClientRect();
    this.actualWidth = rect.width || this.chartContainer.nativeElement.clientWidth || (typeof this.width === 'number' ? this.width : 400);
    this.actualHeight = rect.height || this.chartContainer.nativeElement.clientHeight || (typeof this.height === 'number' ? this.height : 300);

    const svg = container.append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${this.actualWidth} ${this.actualHeight}`)
      .style('overflow', 'visible');

    const defs = svg.append('defs');

    // Determine Mode
    const isGrouped = this.data.some(d => d.values && d.values.length > 0);
    const isSingleValue = !isGrouped && this.data.every(d => d.value !== undefined);

    let maxValue = 0;
    if (isGrouped) {
      maxValue = d3.max(this.data, d => d3.max(d.values || [], v => v.value) || 0) || 0;
    } else if (isSingleValue) {
      maxValue = d3.max(this.data, d => d.value) || 0;
    } else {
      maxValue = d3.max(this.data, d => (d.success || 0) + (d.failure || 0)) || 0;
    }

    const margin = this.margins || { top: 40, right: 20, bottom: 40, left: this.showYAxis ? 50 : 40 };
    const chartWidth = this.actualWidth - margin.left - margin.right;
    const chartHeight = this.actualHeight - margin.top - margin.bottom;

    if (this.showLegend) {
      this.appendLegends(svg, this.actualWidth, this.actualHeight, isDark);
    }

    const xScale = d3.scaleBand()
      .range([this.xAxisPadding, chartWidth])
      .padding(0.4)
      .domain(this.data.map(d => d.label));

    let xSubScale: d3.ScaleBand<string> | null = null;
    if (isGrouped) {
      const maxGroupsCount = d3.max(this.data, d => d.values?.length || 0) || 1;
      const subDomain = Array.from({ length: maxGroupsCount }, (_, i) => i.toString());
      xSubScale = d3.scaleBand()
        .domain(subDomain)
        .range([0, xScale.bandwidth()])
        .paddingInner(this.innerPadding);
    }

    let calculatedBarWidth = this.fitBarToBandwidth
      ? (isGrouped && xSubScale ? xSubScale.bandwidth() : xScale.bandwidth())
      : (isGrouped && xSubScale
        ? Math.min(this.barWidth, xSubScale.bandwidth())
        : Math.min(this.barWidth, xScale.bandwidth()));

    if (this.maxBarWidth > 0 && calculatedBarWidth > this.maxBarWidth) {
      if (isGrouped && this.fitBarToBandwidth && xSubScale) {
        const maxGroupsCount = d3.max(this.data, d => d.values?.length || 0) || 1;
        const desiredGroupWidth = Math.min(xScale.bandwidth(), this.maxBarWidth * maxGroupsCount);
        xSubScale.range([0, desiredGroupWidth]);
        calculatedBarWidth = xSubScale.bandwidth();
      } else {
        calculatedBarWidth = this.maxBarWidth;
      }
    }

    const yScale = d3.scaleLinear()
      .range([chartHeight, 0])
      .domain([0, maxValue * 1.1]); // Add headroom

    const chartGroup = svg.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Draw Grid
    if (this.showGrid) {
      const gridColor = isDark ? 'rgba(71, 71, 78, 0.4)' : 'rgba(229, 231, 235, 0.6)';

      chartGroup.append('g')
        .attr('class', 'grid-lines')
        .call(d3.axisLeft(yScale)
          .tickSize(-chartWidth)
          .tickFormat(() => '') // No labels on left
          .ticks(5)
        )
        .selectAll('line')
        .attr('stroke', gridColor)
        .attr('stroke-dasharray', '3,3')
        .attr('stroke-width', 1);

      chartGroup.append('g')
        .attr('class', 'grid-lines')
        .attr('transform', `translate(0, ${chartHeight})`)
        .call(d3.axisBottom(xScale)
          .tickSize(-chartHeight)
          .tickFormat(() => '')
        )
        .selectAll('line')
        .attr('stroke', gridColor)
        .attr('stroke-dasharray', '3,3')
        .attr('stroke-width', 1);

      chartGroup.selectAll('.domain').remove();
    }

    // Axis Lines
    const axisColor = isDark ? 'rgba(161, 161, 170, 0.5)' : 'rgba(107, 114, 128, 0.5)';

    if (this.showYAxisLine) {
      chartGroup.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', 0)
        .attr('y2', chartHeight)
        .attr('stroke', axisColor)
        .attr('stroke-width', 1);
    }

    if (this.showXAxisLine) {
      chartGroup.append('line')
        .attr('x1', 0)
        .attr('y1', chartHeight)
        .attr('x2', chartWidth)
        .attr('y2', chartHeight)
        .attr('stroke', axisColor)
        .attr('stroke-width', 1);
    }

    // Y Axis
    if (this.showYAxis) {
      const yAxis = d3.axisLeft(yScale)
        .ticks(5)
        .tickFormat(d => d.toLocaleString())
        .tickSize(0)
        .tickPadding(10);

      const yAxisG = chartGroup.append('g')
        .attr('class', 'y-axis')
        .call(yAxis);

      yAxisG.select('.domain').remove();
      yAxisG.selectAll('text')
        .attr('fill', labelColor)
        .attr('font-size', '11px')
        .attr('font-weight', '500');
    }

    const tooltip = d3.select(this.tooltipElement?.nativeElement);

    // Draw bars
    this.data.forEach((d, i) => {
      const x = xScale(d.label) || 0;

      const columnGroup = chartGroup.append('g')
        .attr('class', 'column-group');

      // Setup tooltip events for the column group
      if (this.showTooltip) {
        columnGroup
          .on('mouseover', (event) => {
            const [mouseX, mouseY] = d3.pointer(event, container.node());
            const tooltipNode = tooltip.node() as HTMLElement;
            const tooltipWidth = tooltipNode.getBoundingClientRect().width;
            const containerWidth = container.node().clientWidth;

            let left = mouseX + 15;
            if (left + tooltipWidth > containerWidth) {
              left = mouseX - tooltipWidth - 15;
            }

            const tooltipHeight = tooltipNode.getBoundingClientRect().height;
            let top = mouseY - 15;
            if (top < 0) {
              top = mouseY + 15;
            } else if (top + tooltipHeight > container.node().clientHeight) {
              top = mouseY - tooltipHeight - 15;
            }

            tooltip.style('display', 'block')
              .style('left', `${left}px`)
              .style('top', `${top}px`);

            let html = `<div class="p-3 min-w-[180px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl">`;
            html += `<div class="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2 border-bottom pb-2 border-slate-100 dark:border-slate-700">${d.label}</div>`;

            if (isGrouped && d.values) {
              d.values.forEach(v => {
                const label = v.key || 'Value';
                html += `<div class="flex items-center justify-between gap-4 mb-2">
                                <div class="flex items-center gap-2">
                                    <div class="w-2.5 h-2.5 rounded-sm" style="border: 1px solid ${v.color}; background-color: transparent"></div>
                                    <span class="text-xs text-slate-600 dark:text-slate-400 font-medium">${label}:</span>
                                </div>
                                <span class="text-xs font-bold text-slate-900 dark:text-slate-100">${v.value.toLocaleString()}</span>
                            </div>`;
              });
            } else if (isSingleValue) {
              html += `<div class="flex items-center justify-between gap-4">
                            <span class="text-xs text-slate-600 dark:text-slate-400 font-medium">Value:</span>
                            <span class="text-xs font-bold text-slate-900 dark:text-slate-100">${(d.value || 0).toLocaleString()}</span>
                        </div>`;
            } else {
              html += `<div class="flex items-center justify-between gap-4 mb-1">
                            <div class="flex items-center gap-2">
                                <div class="w-2 h-2 rounded-sm" style="border: 1px solid ${this.successColor}; background-color: transparent"></div>
                                <span class="text-[11px] text-gray-700 dark:text-gray-300">Success:</span>
                            </div>
                            <span class="text-[11px] font-bold text-gray-900 dark:text-gray-100">${(d.success || 0).toLocaleString()}</span>
                        </div>`;
              html += `<div class="flex items-center justify-between gap-4">
                            <div class="flex items-center gap-2">
                                <div class="w-2 h-2 rounded-sm" style="border: 1px solid ${this.failureColor}; background-color: transparent"></div>
                                <span class="text-[11px] text-gray-700 dark:text-gray-300">Failure:</span>
                            </div>
                            <span class="text-[11px] font-bold text-gray-900 dark:text-gray-100">${(d.failure || 0).toLocaleString()}</span>
                        </div>`;
            }
            html += `</div>`;
            tooltip.html(html);
          })
          .on('mousemove', (event) => {
            const [mouseX, mouseY] = d3.pointer(event, container.node());
            const tooltipNode = tooltip.node() as HTMLElement;
            const tooltipWidth = tooltipNode.getBoundingClientRect().width;
            const containerWidth = container.node().clientWidth;

            let left = mouseX + 15;
            if (left + tooltipWidth > containerWidth) {
              left = mouseX - tooltipWidth - 15;
            }

            const tooltipHeight = tooltipNode.getBoundingClientRect().height;
            let top = mouseY - 15;
            if (top < 0) {
              top = mouseY + 15;
            } else if (top + tooltipHeight > container.node().clientHeight) {
              top = mouseY - tooltipHeight - 15;
            }

            tooltip.style('left', `${left}px`)
              .style('top', `${top}px`);
          })
          .on('mouseout', () => {
            tooltip.style('display', 'none');
          });
      }

      if (isGrouped && d.values && d.values.length > 0 && xSubScale) {
        const groupWidth = xSubScale.range()[1];
        const groupStartX = x + (xScale.bandwidth() - groupWidth) / 2;

        d.values.forEach((v, idx) => {
          const subXPos = groupStartX + (xSubScale?.(idx.toString()) || 0) + (xSubScale!.bandwidth() - calculatedBarWidth) / 2;
          const val = v.value;
          const barH = yScale(0) - yScale(val);
          const yPos = yScale(val);

          this.drawIsometricBar(
            columnGroup,
            defs,
            subXPos,
            yPos,
            calculatedBarWidth,
            barH,
            v.color || this.successColor,
            d,
            i * 10 + idx
          );
        });

      } else if (isSingleValue) {
        const centeredX = x + (xScale.bandwidth() - calculatedBarWidth) / 2;
        const val = d.value || 0;
        const barH = yScale(0) - yScale(val);
        const yPos = yScale(val);

        this.drawIsometricBar(
          columnGroup,
          defs,
          centeredX,
          yPos,
          calculatedBarWidth,
          barH,
          d.color || this.successColor,
          d,
          i
        );

        // Value Label
        if (this.showBarValues) {
          columnGroup.append('text')
            .attr('x', centeredX + calculatedBarWidth / 2)
            .attr('y', yPos - 15)
            .attr('text-anchor', 'middle')
            .attr('fill', labelColor)
            .attr('font-size', '10px')
            .attr('font-weight', 'medium')
            .text(val.toLocaleString());
        }

      } else {
        const centeredX = x + (xScale.bandwidth() - calculatedBarWidth) / 2;
        const success = d.success || 0;
        const failure = d.failure || 0;

        const successHeight = Math.max(0, yScale(0) - yScale(success));
        const failureHeight = Math.max(0, yScale(0) - yScale(failure));
        const currentY = chartHeight;

        // Draw Failure (Bottom)
        if (failureHeight > 0) {
          this.drawIsometricBar(
            columnGroup,
            defs,
            centeredX,
            currentY - failureHeight,
            calculatedBarWidth,
            failureHeight,
            this.failureColor,
            d,
            i * 2
          );
        }

        // Draw Success (Top)
        if (successHeight > 0) {
          this.drawIsometricBar(
            columnGroup,
            defs,
            centeredX,
            currentY - failureHeight - successHeight,
            calculatedBarWidth,
            successHeight,
            this.successColor,
            d,
            (i * 2) + 1
          );
        }

        // Text Label on Top
        if (this.showBarValues) {
          const displayValue = success > 0 ? success : failure;
          const textY = currentY - successHeight - failureHeight - 15;

          if (displayValue > 0) {
            columnGroup.append('text')
              .attr('x', centeredX + calculatedBarWidth / 2)
              .attr('y', textY)
              .attr('text-anchor', 'middle')
              .attr('fill', textColor)
              .attr('font-size', '11px')
              .attr('font-weight', '700')
              .text(`${displayValue}${this.showPercentage ? '%' : ''}`);
          }
        }
      }

      // Add an invisible rect to capture mouse events over the entire column group area
      if (this.showTooltip) {
        const bgHeight = this.reducedHighlight ? chartHeight : this.height;
        const bgY = this.reducedHighlight ? 0 : -margin.top;

        const bgRect = columnGroup.append('rect')
          .attr('x', x)
          .attr('y', bgY)
          .attr('width', xScale.bandwidth())
          .attr('height', bgHeight)
          .attr('fill', isDark ? '#ffffff' : '#000000')
          .attr('fill-opacity', this.showBgRect ? this.hoverOpacity : 0)
          .attr('pointer-events', 'all')
          .attr('rx', 4)
          .style('display', this.showBgRect ? 'block' : 'none');

        columnGroup.on('mouseover.highlight', () => {
          if (this.showBgRect) {
            bgRect.transition().duration(200).attr('fill-opacity', this.hoverOpacity * 2.5);
          }
        });
        columnGroup.on('mouseout.highlight', () => {
          if (this.showBgRect) {
            bgRect.transition().duration(200).attr('fill-opacity', this.hoverOpacity);
          }
        });
      }
    });

    // X Axis
    const xAxis = d3.axisBottom(xScale)
      .tickSize(0)
      .tickPadding(15)
      .tickFormat((d: string, i: number) => {
        if (this.xAxisTickFormat) {
          return this.xAxisTickFormat(d);
        }
        return d;
      });

    if (this.xAxisShowOnlyFirstLast) {
      const domain = xScale.domain();
      if (domain.length > 1) {
        xAxis.tickValues([domain[0], domain[domain.length - 1]]);
      }
    }

    const xAxisGroup = chartGroup.append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(xAxis);

    xAxisGroup.select('.domain').remove();

    // Style ticks
    chartGroup.selectAll('.tick text')
      .attr('fill', labelColor)
      .attr('font-size', '11px')
      .attr('font-family', 'Inter, sans-serif');
  }

  private appendLegends(svg: d3.Selection<any, any, any, any>, width: number, height: number, isDark: boolean) {
    if (!this.legendData || this.legendData.length === 0) return;
    const totalW = (this.legendData.length - 1) * this.legendGap;
    let lx = (width - totalW) / 2;
    if (this.legendPosition === 'left') lx = this.margins.left || 60;
    else if (this.legendPosition === 'right') lx = width - totalW - (this.margins.right || 30);

    const leg = svg.append('g').attr('transform', `translate(${lx}, ${height - 10})`);
    this.legendData.forEach((d, i) => {
      const g = leg.append('g').attr('transform', `translate(${i * this.legendGap}, 0)`);
      g.append('circle').attr('cx', 6).attr('cy', 6).attr('r', 5).attr('fill', d.color || (isDark ? '#a1a1aa' : '#64748b'));
      g.append('text').attr('x', 18).attr('y', 10).text(d.label).style('font-size', '12px').style('fill', isDark ? '#a1a1aa' : '#64748b');
    });
  }

  private drawIsometricBar(
    container: any,
    defs: any,
    x: number,
    y: number,
    width: number,
    height: number,
    color: string,
    data: IsometricBarData,
    index: number
  ) {
    const angleRad = (Math.PI / 180) * this.angle;
    const depthX = this.barDepth * Math.cos(angleRad);
    const depthY = this.barDepth * Math.sin(angleRad);
    const r = Math.min(this.borderRadius, width / 2, height);

    const barGroup = container.append('g');

    const frontColor = color;
    const sideColor = d3.color(color)?.darker(this.sideFaceDarkenFactor)?.toString() || color;
    const baseColor = color;

    // 1. Right Face
    const rightFacePath = `
      M ${x + width},${y + r} 
      L ${x + width + depthX},${y + r - depthY} 
      L ${x + width + depthX},${y + height - depthY} 
      L ${x + width},${y + height} 
      Z
    `;
    barGroup.append('path')
      .attr('d', rightFacePath)
      .attr('fill', sideColor);

    // 2. Gradient Defs
    const gradientId = `${this.chartId}-gradient-${index}`;

    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', `${Math.cos(angleRad) * 100}%`)
      .attr('y2', `${Math.sin(angleRad) * 100}%`);

    gradient.append('stop').attr('offset', '0%').attr('stop-color', frontColor);
    gradient.append('stop').attr('offset', '100%').attr('stop-color', sideColor);

    const topGradientId = `${this.chartId}-top-gradient-${index}`;
    const highlightColor = d3.color(color)?.brighter(0.5)?.toString() || color;

    const topGradient = defs.append('linearGradient')
      .attr('id', topGradientId)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');

    topGradient.append('stop').attr('offset', '0%').attr('stop-color', highlightColor);
    topGradient.append('stop').attr('offset', '100%').attr('stop-color', baseColor);
    
    // 3. Right Corner
    const rightCornerPath = `
      M ${x + width - r},${y}
      A ${r},${r} 0 0 1 ${x + width},${y + r}
      L ${x + width + depthX},${y + r - depthY}
      A ${r},${r} 0 0 0 ${x + width - r + depthX},${y - depthY}
      Z
    `;
    barGroup.append('path')
      .attr('d', rightCornerPath)
      .attr('fill', `url(#${gradientId})`)
      .attr('stroke', 'none');

    // 4. Left Corner
    const leftCornerPath = `
      M ${x + r},${y}
      A ${r},${r} 0 0 0 ${x},${y + r}
      L ${x + depthX},${y + r - depthY}
      A ${r},${r} 0 0 1 ${x + r + depthX},${y - depthY}
      Z
    `;
    barGroup.append('path')
      .attr('d', leftCornerPath)
      .attr('fill', `url(#${gradientId})`)
      .attr('stroke', 'none');

    // 5. Top Face
    const topFacePath = `
      M ${x + r},${y}
      L ${x + width - r},${y}
      L ${x + width - r + depthX},${y - depthY}
      L ${x + r + depthX},${y - depthY}
      Z
    `;
    barGroup.append('path')
      .attr('d', topFacePath)
      .attr('fill', `url(#${topGradientId})`)
      .attr('stroke', frontColor)
      .attr('stroke-width', 0.5);

    // 6. Front Face
    const frontFacePath = `
      M ${x},${y + height} 
      L ${x},${y + r} 
      A ${r},${r} 0 0 1 ${x + r},${y} 
      L ${x + width - r},${y} 
      A ${r},${r} 0 0 1 ${x + width},${y + r} 
      L ${x + width},${y + height} 
      Z
    `;
    barGroup.append('path')
      .attr('d', frontFacePath)
      .attr('fill', frontColor);
  }
}
